import {
  HardDrive, Database, Shield, Download, Mail, Cloud, CloudUpload,
  CalendarClock, History, Settings,
} from "lucide-react";
import { PasswordInput } from "../shared/PasswordInput";
import { TelcavoipCloudCard } from "./TelcavoipCloudCard";
import { useITPortalI18n } from "../../i18n/ITPortalI18nContext";

const fmt = (tpl, vars) =>
  tpl.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));

export const BackupTab = ({
  backupStatus, backupHistory, backupTab, setBackupTab,
  downloadBackup, backupDownloading,
  sendBackupEmail, backupEmailing,
  uploadToCloud, uploadingCloud,
  toggleAutoBackup, backupSchedule, updateBackupInterval,
  cloudConfig, setCloudConfig, saveCloudConfig, savingCloud,
}) => {
  const { t, locale } = useITPortalI18n();
  const localeTag = locale === 'IT' ? 'it-IT' : 'en-GB';
  return (
  <div className="space-y-6 animate-fade-in">
    <h2 className="text-xl font-bold text-white">{t.b_title}</h2>

    {/* Stats */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-2"><HardDrive size={18} className="text-blue-400" /><p className="text-gray-500 text-xs">{t.b_last_backup}</p></div>
        <p className="text-white font-semibold text-lg">{backupStatus?.last_backup ? new Date(backupStatus.last_backup).toLocaleString(localeTag) : t.b_never_done}</p>
        {backupStatus?.days_ago !== null && backupStatus?.days_ago !== undefined && (
          <p className={`text-xs mt-1 ${backupStatus?.days_ago >= 7 ? 'text-lojack-clause-500' : 'text-lojack-blue-500'}`}>
            {backupStatus?.days_ago === 0 ? t.b_today : fmt(t.b_days_ago, { n: backupStatus?.days_ago })}
          </p>
        )}
      </div>
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-2"><Database size={18} className="text-lojack-blue-400" /><p className="text-gray-500 text-xs">{t.b_total_backups}</p></div>
        <p className="text-white font-semibold text-lg">{backupHistory.length}</p>
        <p className="text-gray-400 text-xs mt-1">{fmt(t.b_manual_email_cloud, {
          m: backupHistory.filter(b => b.type === 'manual').length,
          e: backupHistory.filter(b => b.type === 'email').length,
          c: backupHistory.filter(b => b.type === 'cloud').length,
        })}</p>
      </div>
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-2"><Shield size={18} className="text-lojack-blue-400" /><p className="text-gray-500 text-xs">{t.b_state}</p></div>
        <p className={`font-semibold text-lg ${backupStatus?.needs_backup ? 'text-lojack-clause-400' : 'text-lojack-blue-400'}`}>{backupStatus?.needs_backup ? t.b_backup_recommended : t.b_up_to_date}</p>
      </div>
    </div>

    {/* Sub-tabs */}
    <div className="flex gap-2 border-b border-gray-800 pb-3">
      {[
        { key: 'main', label: t.b_tab_main, icon: HardDrive },
        { key: 'schedule', label: t.b_tab_schedule, icon: CalendarClock },
        { key: 'cloud', label: t.b_tab_cloud, icon: Cloud },
        { key: 'history', label: t.b_tab_history, icon: History },
      ].map(tab => (
        <button key={tab.key} onClick={() => setBackupTab(tab.key)}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${backupTab === tab.key ? 'bg-lojack-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
          data-testid={`it-backup-tab-${tab.key}`}>
          <tab.icon size={14} />{tab.label}
        </button>
      ))}
    </div>

    {/* Main */}
    {backupTab === 'main' && (
      <>
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
        <p className="text-white font-medium mb-1">{t.b_main_title}</p>
        <p className="text-gray-500 text-xs mb-5">{t.b_main_subtitle}</p>
        <div className="flex gap-3 flex-wrap">
          <button onClick={downloadBackup} disabled={backupDownloading}
            className="flex items-center gap-2 px-5 py-3 bg-lojack-blue-600 text-white rounded-xl font-medium hover:bg-lojack-blue-700 transition-colors disabled:opacity-50" data-testid="it-download-backup-btn">
            {backupDownloading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Download size={18} />}
            {backupDownloading ? t.b_downloading : t.b_download_backup}
          </button>
          <button onClick={sendBackupEmail} disabled={backupEmailing}
            className="flex items-center gap-2 px-5 py-3 bg-[#0A0A0A] border border-gray-700 text-white rounded-xl font-medium hover:border-lojack-blue-600 transition-colors disabled:opacity-50" data-testid="it-email-backup-btn">
            {backupEmailing ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Mail size={18} />}
            {backupEmailing ? t.b_sending : t.b_send_email}
          </button>
          {cloudConfig.enabled && (
            <button onClick={uploadToCloud} disabled={uploadingCloud}
              className="flex items-center gap-2 px-5 py-3 bg-blue-600/10 border border-blue-600/30 text-blue-400 rounded-xl font-medium hover:bg-blue-600/20 transition-colors disabled:opacity-50" data-testid="it-upload-cloud-btn">
              {uploadingCloud ? <div className="w-4 h-4 border-2 border-blue-300/30 border-t-blue-300 rounded-full animate-spin" /> : <CloudUpload size={18} />}
              {uploadingCloud ? t.b_uploading : t.b_upload_cloud}
            </button>
          )}
        </div>
      </div>
      <TelcavoipCloudCard />
      </>
    )}

    {/* Schedule */}
    {backupTab === 'schedule' && (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-white font-medium">{t.b_auto_backup}</p>
            <p className="text-gray-500 text-xs mt-1">{t.b_auto_backup_help}</p>
          </div>
          <button onClick={toggleAutoBackup} className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${backupSchedule?.enabled ? 'bg-lojack-blue-600' : 'bg-gray-700'}`} data-testid="it-auto-backup-toggle">
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${backupSchedule?.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
        {backupSchedule?.enabled && (
          <div>
            <p className="text-gray-400 text-xs mb-3">{t.b_frequency}</p>
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: t.b_freq_daily, days: 1, desc: t.b_freq_daily_desc },
                { label: t.b_freq_weekly, days: 7, desc: t.b_freq_weekly_desc },
                { label: t.b_freq_biweekly, days: 15, desc: t.b_freq_biweekly_desc },
                { label: t.b_freq_monthly, days: 30, desc: t.b_freq_monthly_desc }
              ].map(opt => (
                <button key={opt.days} onClick={() => updateBackupInterval(opt.days)}
                  className={`p-3 rounded-xl text-center transition-colors border ${backupSchedule?.interval_days === opt.days ? 'bg-lojack-blue-600/10 border-lojack-blue-600/30 text-white' : 'bg-[#0A0A0A] border-gray-700 text-gray-400 hover:border-gray-600'}`}
                  data-testid={`it-backup-interval-${opt.days}`}>
                  <p className="text-sm font-medium">{opt.label}</p>
                  <p className="text-[10px] text-gray-500 mt-1">{opt.desc}</p>
                </button>
              ))}
            </div>
            <p className="text-lojack-blue-500 text-xs mt-3 flex items-center gap-1"><CalendarClock size={12} />{t.b_freq_footer}</p>
          </div>
        )}
      </div>
    )}

    {/* Cloud Config */}
    {backupTab === 'cloud' && (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-white font-medium flex items-center gap-2"><Cloud size={16} className="text-blue-400" /> {t.b_cloud_external}</p>
            <p className="text-gray-500 text-xs mt-1">{t.b_cloud_external_help}</p>
          </div>
          <button onClick={() => setCloudConfig(prev => ({...prev, enabled: !prev.enabled}))}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${cloudConfig.enabled ? 'bg-lojack-blue-600' : 'bg-gray-700'}`} data-testid="it-cloud-toggle">
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${cloudConfig.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
        {cloudConfig.enabled && (
          <div className="space-y-3 mt-4">
            <div>
              <label className="text-gray-400 text-xs">{t.b_provider}</label>
              <select value={cloudConfig.provider} onChange={e => setCloudConfig(prev => ({...prev, provider: e.target.value}))}
                className="w-full mt-1 px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm" data-testid="it-cloud-provider">
                <option value="aws_s3" style={{backgroundColor:'#111'}}>Amazon S3</option>
                <option value="google_cloud" style={{backgroundColor:'#111'}}>Google Cloud Storage</option>
                <option value="azure_blob" style={{backgroundColor:'#111'}}>Azure Blob Storage</option>
                <option value="minio" style={{backgroundColor:'#111'}}>{t.b_minio_label}</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-gray-400 text-xs">{t.b_endpoint_url}</label><input value={cloudConfig.endpoint_url} onChange={e => setCloudConfig(prev => ({...prev, endpoint_url: e.target.value}))} placeholder="https://s3.eu-west-1.amazonaws.com" className="w-full mt-1 px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm" /></div>
              <div><label className="text-gray-400 text-xs">{t.b_bucket_name}</label><input value={cloudConfig.bucket_name} onChange={e => setCloudConfig(prev => ({...prev, bucket_name: e.target.value}))} placeholder="lojack-backups" className="w-full mt-1 px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-gray-400 text-xs">{t.b_access_key}</label><input value={cloudConfig.access_key} onChange={e => setCloudConfig(prev => ({...prev, access_key: e.target.value}))} placeholder="AKIA..." className="w-full mt-1 px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm font-mono" /></div>
              <div><label className="text-gray-400 text-xs">{t.b_secret_key}</label><PasswordInput value={cloudConfig.secret_key} onChange={e => setCloudConfig(prev => ({...prev, secret_key: e.target.value}))} placeholder="••••••••" className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm font-mono" data-testid="it-cloud-secret" /></div>
            </div>
            <div><label className="text-gray-400 text-xs">{t.b_region}</label><input value={cloudConfig.region} onChange={e => setCloudConfig(prev => ({...prev, region: e.target.value}))} placeholder="eu-west-1" className="w-full mt-1 px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none text-sm" /></div>
            <div className="flex gap-3 pt-2">
              <button onClick={saveCloudConfig} disabled={savingCloud}
                className="flex items-center gap-2 px-4 py-2.5 bg-lojack-blue-600 text-white rounded-xl font-medium hover:bg-lojack-blue-700 transition-colors disabled:opacity-50" data-testid="it-save-cloud-btn">
                {savingCloud ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Settings size={16} />}
                {savingCloud ? t.b_saving_config : t.b_save_config}
              </button>
              <button onClick={uploadToCloud} disabled={uploadingCloud || !cloudConfig.bucket_name}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 text-white rounded-xl font-medium hover:border-lojack-blue-600 transition-colors disabled:opacity-50" data-testid="it-test-cloud-btn">
                {uploadingCloud ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <CloudUpload size={16} />}
                {uploadingCloud ? t.b_uploading : t.b_test_upload}
              </button>
            </div>
          </div>
        )}
        {!cloudConfig.enabled && <p className="text-gray-600 text-xs mt-2">{t.b_enable_to_configure}</p>}
      </div>
    )}

    {/* History */}
    {backupTab === 'history' && (
      <div>
        {backupHistory.length > 0 ? (
          <div className="bg-[#111] border border-gray-800 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-800 bg-[#0D0D0D]"><h3 className="text-white font-semibold flex items-center gap-2"><History size={16} />{t.b_history_title}</h3></div>
            <div className="divide-y divide-gray-800/50 max-h-[400px] overflow-y-auto">
              {backupHistory.map((b, i) => (
                <div key={i} className="flex items-center justify-between px-6 py-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${b.type === 'manual' ? 'bg-blue-500/10 text-blue-400' : b.type === 'email' ? 'bg-lojack-blue-500/10 text-lojack-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>
                      {b.type === 'manual' ? <Download size={14} /> : b.type === 'email' ? <Mail size={14} /> : <CloudUpload size={14} />}
                    </div>
                    <div><p className="text-white text-sm">{b.type === 'manual' ? t.b_history_manual : b.type === 'email' ? t.b_history_email : t.b_history_cloud}</p><p className="text-gray-500 text-xs">{new Date(b.created_at).toLocaleString(localeTag)}</p></div>
                  </div>
                  <div className="text-right">{b.size_kb && <p className="text-gray-400 text-xs">{b.size_kb} KB</p>}{b.stats && <p className="text-gray-600 text-[10px]">{fmt(t.b_customers_count, { n: b.stats.customers_count })}</p>}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-8 text-center">
            <Database size={36} className="mx-auto mb-3 text-gray-700" />
            <p className="text-gray-500 text-sm">{t.b_no_backups}</p>
          </div>
        )}
      </div>
    )}
  </div>
  );
};

export default BackupTab;
